import NextLink from 'next/link';
import { oneLine } from 'common-tags';

import { Button, Link, RichInline } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { ContentList, Gallery, Prose, TwoColumn } from '@/molecules';

import { generatedContentImageConfig } from '@/lib/contexts/content-image-config.generated';
import { toArray } from '@/utils';

import type { CSSProperties, FunctionComponent } from 'react';
import type { TypeContentImageProps } from './types';

export const ContentImage: FunctionComponent<TypeContentImageProps> = ({
  className = '',
  reverse = false,
  image,
  description,
  font = 'large',
  eyebrow,
  title,
  subtitle,
  titleSize = 'h3',
  imageHeight = 'default',
  imagePosition = 'center',
  imageShapeUrl,
  eyebrowStyleClass,
  titleStyleClass,
  subtitleStyleClass,
  descriptionStyleClass,
  link,
  button,
  list,
  isViewForms,
  flat,
}: TypeContentImageProps) => {
  const imagesToShow = toArray(image);
  /**
   * Cuando AlturaImagen=cuadrada, forzamos aspect-square en el wrapper y
   * sobreescribimos los `h-[300px]` / `h-[402px]` que Gallery hardcodea en sus
   * divs internos para que la imagen llene el cuadrado real, no se quede en
   * el alto fijo histórico.
   *
   * Cuando AlturaImagen=default, no aplicamos nada — Gallery mantiene su
   * comportamiento histórico (incluyendo cualquier max-height interna).
   */
  const heightClass =
    imageHeight === 'cuadrada'
      ? 'aspect-square overflow-hidden [&>div]:!h-full [&_div]:!h-full [&_img]:!h-full [&_img]:!w-full [&_img]:object-cover'
      : '';
  /**
   * Sobreescribe el `object-center` que Gallery hardcodea en su `<img>`.
   * `imagePosition` acepta cualquier valor CSS de `object-position`: keywords
   * (`top`, `bottom`) o valores arbitrarios (`0 20%`, `50% 25%`). Como el valor
   * llega de Strapi en runtime, NO puede vivir en una clase arbitraria (el
   * scanner de Tailwind solo compila clases literales): se inyecta vía la CSS
   * var `--ci-object-pos` y una utility estática la lee. El selector `[&_img]`
   * (`.wrapper img`) gana al `object-center` del propio img por especificidad.
   * `center` (default) → sin override = comportamiento histórico.
   */
  const hasPosition = !!imagePosition && imagePosition !== 'center';
  const positionClass = hasPosition
    ? '[&_img]:[object-position:var(--ci-object-pos)]'
    : '';
  const maskStyle: CSSProperties | undefined = imageShapeUrl
    ? {
        WebkitMaskImage: `url(${imageShapeUrl})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: '100% 100%',
        maskImage: `url(${imageShapeUrl})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: '100% 100%',
      }
    : undefined;
  const galleryWrapperStyle: CSSProperties | undefined = hasPosition
    ? ({ ...maskStyle, '--ci-object-pos': imagePosition } as CSSProperties)
    : maskStyle;
  // Strapi devuelve `null` (no `undefined`) cuando TamanoTitulo está vacío,
  // y `null` se salta el default del destructuring → sin el `?? 'h3'` la
  // clase renderizaba literalmente `text-headline-null` (título a 16px).
  const titleSizeClass = `text-headline-${titleSize ?? 'h3'}`;
  /**
   * Cuando hay un Estilo seleccionado (`*StyleClass`), se usa esa clase
   * generada del catálogo y se omite el slot por defecto del bloque.
   * Si no, se aplica el comportamiento histórico.
   */
  const descriptionPSizeClass =
    font === 'small'
      ? '[&_p]:text-body-s'
      : font === 'medium'
        ? '[&_p]:text-body-m'
        : '[&_p]:text-body-l';

  /**
   * Con `Inverso=true`, el comportamiento histórico de TwoColumn es
   * mantener la imagen arriba en mobile (`flex-col-reverse` revierte el
   * `order-1/order-2`). Si el tenant activa
   * `contentImageInversoMobileTextoArriba` en ThemeConfiguration, dejamos
   * que el orden natural se aplique (texto arriba, imagen abajo) — es lo
   * que pide el Figma de HoM. Default false → no se mueve nada para los
   * tenants antiguos.
   */
  const reverseClass = reverse
    ? generatedContentImageConfig.inversoMobileTextoArriba
      ? 'flex! flex-col lg:flex-row'
      : 'flex! flex-col-reverse lg:flex-row'
    : '';

  return (
    <TwoColumn
      reverse={reverse}
      gap="gap-8"
      className={oneLine` ${reverseClass} ${className} `}
    >
      <TwoColumn.Left
        className={oneLine`${heightClass} ${positionClass}`}
        style={galleryWrapperStyle}
      >
        <Gallery
          isGrid={false}
          images={imagesToShow}
          typeModal={isViewForms ? 'form' : 'grid'}
          {...(flat && { flat: flat })}
        />
      </TwoColumn.Left>

      <TwoColumn.Right className="flex flex-col items-start justify-center">
        {/* El bloque se ciñe al ancho de su hijo más largo (normalmente el
            eyebrow). Con `items-end`, un título más corto quedaba pegado al
            borde derecho de ese ancho y se leía como centrado. El título va
            siempre a la izquierda; el eyebrow, al ser el elemento que marca el
            ancho, se ve igual que antes. */}
        {(eyebrow || title) && (
          <div className="mb-4 flex flex-col items-start self-start">
            {eyebrow && (
              <RichInline
                html={eyebrow}
                className={oneLine`font-accent text-primary-500 ${
                  eyebrowStyleClass ?? 'text-3xl leading-10 font-normal'
                }`}
              />
            )}
            {title && (
              <RichInline
                as="h2"
                html={title}
                className={oneLine`text-headline text-text-primary font-primary ${
                  titleStyleClass ?? `${titleSizeClass} font-bold`
                }`}
              />
            )}
          </div>
        )}

        {subtitle && (
          <RichInline
            as="p"
            html={subtitle}
            className={oneLine`text-body text-text-primary mb-4 ${
              subtitleStyleClass ?? 'text-body-l'
            }`}
          />
        )}

        {description && (
          <Prose
            className={oneLine`text-body text-text-primary ${
              descriptionStyleClass ?? descriptionPSizeClass
            }`}
            html={description}
          />
        )}

        {list?.items?.length ? (
          <ContentList tipo={list.tipo} label={list.label} items={list.items} />
        ) : null}

        {link && (
          <NextLink href={link.href} prefetch={false} passHref legacyBehavior>
            {link.type === 'link' ? (
              <Link icon="right" className="mt-8 font-bold md:mt-14">
                {link.label}
              </Link>
            ) : (
              <Button
                size={ButtonSize.SMALL}
                variant={link.variant ?? ButtonVariant.PRIMARY}
                as="a"
                className="mt-14"
              >
                {link.label}
              </Button>
            )}
          </NextLink>
        )}

        {button && (
          <Button
            size={ButtonSize.SMALL}
            variant={button.variant ?? ButtonVariant.PRIMARY}
            className="mt-14"
            onClick={button.onClick}
          >
            {button.label}
          </Button>
        )}
      </TwoColumn.Right>
    </TwoColumn>
  );
};

ContentImage.displayName = 'ContentImage';
